import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  ChannelModelRecord,
  InternalSkuStatusResponse,
  ModelPlanRepriceImpactRow,
} from '@nadoutong/contracts';
import { useModelPlanRepriceImpact } from '../api/modelPlans.ts';
import { useChannelModelsList, useUpdateChannelModel } from '../api/useChannelModels.ts';
import { useChannelsList } from '../api/useChannels.ts';
import { useSkuStatus } from '../api/useSkuStatus.ts';
import {
  RATE_MULTIPLIER_INPUT_MIN,
  RATE_MULTIPLIER_INPUT_STEP,
  formatLocalDate,
  formatLocalTimestamp,
} from '../components/formatters.ts';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { KeyPresetActivationDrawer } from '../components/KeyPresetActivationDrawer.tsx';
import { ReadinessChip } from '../components/sku-detail/ReadinessChip.tsx';
import { NamespaceRateToggle } from '../components/sku-detail/NamespaceRateToggle.tsx';
import { ShelfToggle } from '../components/sku-detail/ShelfToggle.tsx';
import { SkuLoadScopePanel } from '../components/sku-detail/SkuLoadScopePanel.tsx';
import { SkuRateLimitPanel } from '../components/sku-detail/SkuRateLimitPanel.tsx';
import { SkuSaleWindowsEditor } from '../components/sku-detail/SkuSaleWindowsEditor.tsx';
import { SupplyPanel } from '../components/sku-detail/SupplyPanel.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  FormField,
  InfoHint,
  Input,
  PageHeader,
  Panel,
  Skeleton,
  Tooltip,
} from '../ui/index.ts';
import type { SkuOption } from './tenantRoutingShared.ts';

/**
 * SKU detail (epic #898 P6, #910; reshaped by #1428): pricing, 24h status,
 * and ONE supply panel that owns every write about where this SKU's traffic
 * goes — binding, weights, retry order, execution policy, exclusive-coverage
 * repair. Customer-specific route configs stay on the tenant routing page.
 *
 * The page used to carry six sections in "onboarding order", three of which
 * described the same table from different angles (supply binding, default
 * pool, readiness) and two of which were pure signposts (key-preset hint,
 * org overrides). They are merged, moved into the header, or gone.
 */

function skuStateChip(state: 'healthy' | 'busy' | 'unavailable'): {
  readonly tone: 'ok' | 'warn' | 'bad';
  readonly labelKey: I18nKey;
} {
  switch (state) {
    case 'busy':
      return { tone: 'warn', labelKey: 'sku_detail.status.state.busy' };
    case 'unavailable':
      return { tone: 'bad', labelKey: 'sku_detail.status.state.unavailable' };
    case 'healthy':
      return { tone: 'ok', labelKey: 'sku_detail.status.state.healthy' };
  }
}

/**
 * Dependency-free inline sparkline over the 5-minute success-rate
 * buckets. Token stroke classes only (design guardrails).
 */
function StatusSparkline({
  buckets,
  tone,
  ariaLabel,
}: {
  readonly buckets: InternalSkuStatusResponse['buckets'];
  readonly tone: 'ok' | 'warn' | 'bad';
  readonly ariaLabel: string;
}): JSX.Element {
  const width = 480;
  const height = 48;
  const values = buckets.map((bucket) => bucket.success_rate_bps / 100);
  const points = values.length <= 1
    ? ''
    : values
        .map((value, index) => {
          const x = (index / Math.max(1, values.length - 1)) * width;
          const y = height - (Math.min(100, Math.max(0, value)) / 100) * (height - 6) - 3;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(' ');
  const strokeClass = tone === 'bad' ? 'stroke-bad' : tone === 'warn' ? 'stroke-warn' : 'stroke-ok';
  const fillClass = tone === 'bad' ? 'fill-bad' : tone === 'warn' ? 'fill-warn' : 'fill-ok';
  const last = values.length === 0 ? undefined : values[values.length - 1];
  const lastY = last === undefined
    ? height - 3
    : height - (Math.min(100, Math.max(0, last)) / 100) * (height - 6) - 3;
  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${width} ${height}`}
      className="h-12 w-full overflow-visible"
      preserveAspectRatio="none"
      data-testid="sku-status-sparkline"
    >
      <line
        x1="0"
        x2={width}
        y1={height - 3}
        y2={height - 3}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
        className="stroke-line"
      />
      {points.length > 0 ? (
        <polyline
          points={points}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          className={strokeClass}
        />
      ) : null}
      {last !== undefined ? (
        <circle cx={values.length <= 1 ? width - 6 : width} cy={lastY} r="2.5" className={fillClass} />
      ) : null}
    </svg>
  );
}

/**
 * 24h availability panel. Renders nothing until the proxy answers, and
 * stays hidden when no rollup has seen the SKU yet (`snapshot` null and
 * zero buckets) — a brand-new SKU should not show an empty chart shell.
 */
function SkuStatusPanel({ skuId }: { readonly skuId: string }): JSX.Element | null {
  const t = useT();
  const statusQ = useSkuStatus(skuId);
  if (statusQ.isPending || statusQ.isError) {
    return null;
  }
  const { snapshot, buckets } = statusQ.data;
  if (snapshot === null && buckets.length === 0) {
    return null;
  }
  const state = snapshot?.state ?? 'healthy';
  const chip = skuStateChip(state);
  return (
    <Panel caption={t('sku_detail.status.title')} padded testId="sku-status-panel">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          {snapshot !== null ? (
            <>
              <Chip tone={chip.tone} testId="sku-status-state">
                {t(chip.labelKey)}
              </Chip>
              <span className="text-dense text-paper-low">
                {t('sku_detail.status.availability')}{' '}
                <span className="font-mono font-semibold text-paper" data-testid="sku-status-availability">
                  {(snapshot.availability_bps / 100).toFixed(2)}%
                </span>
              </span>
              <span className="text-dense text-paper-low">
                {t('sku_detail.status.requests_24h')}{' '}
                <span className="font-mono font-semibold text-paper">{snapshot.request_count_24h}</span>
              </span>
              <span className="font-mono text-micro text-paper-low">
                {t('sku_detail.status.updated_at')} {formatLocalTimestamp(snapshot.updated_at)}
              </span>
            </>
          ) : null}
        </div>
        {buckets.length > 0 ? (
          <StatusSparkline
            buckets={buckets}
            tone={chip.tone}
            ariaLabel={t('sku_detail.status.sparkline_aria')}
          />
        ) : null}
      </div>
    </Panel>
  );
}

function RepriceImpactWarning({
  plans,
}: {
  readonly plans: readonly ModelPlanRepriceImpactRow[];
}): JSX.Element {
  const t = useT();
  return (
    <div
      role="status"
      data-testid="sku-pricing-reprice-warning"
      className="flex flex-col gap-2 rounded-lg border border-warn-line bg-warn-dim/40 px-3 py-2"
    >
      <p className="m-0 text-dense text-warn">{t('sku_detail.pricing.reprice_warning')}</p>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {plans.map((plan) => (
          <li key={plan.id} className="m-0 text-dense text-paper">
            <Link
              to="/market/model-plans"
              className="font-mono text-signal transition-colors duration-120 hover:text-signal-bright"
              data-testid={`sku-pricing-reprice-plan-${plan.id}`}
            >
              {t('sku_detail.pricing.reprice_plan', {
                name: plan.name,
                brand: plan.brand,
                rate: plan.resolved_rate.toFixed(4),
                orgs: String(plan.assigned_org_count),
              })}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Empty when the SKU has no own cost (the namespace default is not this field). */
function skuCostDraft(value: number | null): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

function SkuPricingPanel({ record }: { readonly record: ChannelModelRecord }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const updateMut = useUpdateChannelModel();
  const [rateDraft, setRateDraft] = useState(String(record.rate_multiplier));
  const [costDraft, setCostDraft] = useState(skuCostDraft(record.upstream_cost_cny_per_usd));

  useEffect(() => {
    setRateDraft(String(record.rate_multiplier));
    setCostDraft(skuCostDraft(record.upstream_cost_cny_per_usd));
  }, [record.rate_multiplier, record.upstream_cost_cny_per_usd, record.sku_id]);

  function handleSave(): void {
    const rate = parseFloat(rateDraft);
    if (!Number.isFinite(rate) || rate <= 0) {
      toast.error(t('sku_detail.pricing.invalid_rate'));
      return;
    }
    const cost = parseFloat(costDraft);
    if (!Number.isFinite(cost) || cost <= 0) {
      toast.error(t('sku_detail.pricing.invalid_cost'));
      return;
    }
    updateMut.mutate(
      {
        skuId: record.sku_id,
        body: { rate_multiplier: rate, upstream_cost_cny_per_usd: cost },
      },
      {
        onSuccess: () => toast.success(t('sku_detail.pricing.toast_saved')),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  const dirty = rateDraft !== String(record.rate_multiplier) ||
    costDraft !== skuCostDraft(record.upstream_cost_cny_per_usd);

  const candidateRate = Number.parseFloat(rateDraft);
  const rateDirty =
    Number.isFinite(candidateRate) &&
    candidateRate > 0 &&
    rateDraft !== String(record.rate_multiplier);
  const reprice = useModelPlanRepriceImpact(
    rateDirty
      ? { grain: 'sku', target_id: record.sku_id, candidate_rate: candidateRate }
      : null,
    rateDirty,
  );
  const repricePlans = reprice.data?.plans ?? [];

  return (
    <Panel caption={t('sku_detail.pricing.title')} padded testId="sku-pricing-panel">
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('sku_detail.pricing.rate_label')}
            fieldId="sku-pricing-rate"
            hint={t('sku_detail.pricing.rate_hint')}
          >
            <Input
              id="sku-pricing-rate"
              type="number"
              mono
              step={RATE_MULTIPLIER_INPUT_STEP}
              min={RATE_MULTIPLIER_INPUT_MIN}
              value={rateDraft}
              onChange={(event) => setRateDraft(event.currentTarget.value)}
              data-testid="sku-pricing-rate"
            />
          </FormField>
          <FormField
            label={t('sku_detail.pricing.cost_label')}
            fieldId="sku-pricing-cost"
            hint={t('sku_detail.pricing.cost_hint')}
          >
            <Input
              id="sku-pricing-cost"
              type="number"
              mono
              step="0.01"
              min="0.01"
              value={costDraft}
              onChange={(event) => setCostDraft(event.currentTarget.value)}
              data-testid="sku-pricing-cost"
            />
          </FormField>
        </div>
        <NamespaceRateToggle record={record} />
        <SkuSaleWindowsEditor
          skuId={record.sku_id}
          skuName={record.sku_name}
          rateMultiplier={record.rate_multiplier}
          windows={record.windows ?? []}
          activeWindowId={record.active_window_id ?? null}
          effectiveRateMultiplier={record.effective_rate_multiplier ?? record.rate_multiplier}
          saving={updateMut.isPending}
          onSave={async (windows) => {
            await updateMut.mutateAsync(
              { skuId: record.sku_id, body: { windows } },
            );
            toast.success(t('sku_detail.pricing.windows.toast_saved'));
          }}
        />
        {repricePlans.length > 0 ? (
          <RepriceImpactWarning plans={repricePlans} />
        ) : null}
        <div className="flex items-center justify-end">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateMut.isPending || !dirty}
            loading={updateMut.isPending}
            data-testid="sku-pricing-save"
          >
            {updateMut.isPending ? t('sku_detail.pricing.saving') : t('sku_detail.pricing.save')}
          </Button>
        </div>
        {/*
          The shelf switch (#1407) sits under pricing rather than in a panel of
          its own: it is gated on this SKU being priced, so the thing an
          operator must fix first is the thing directly above it. The readiness
          checklist's visibility row anchors here.
        */}
        <ShelfToggle record={record} />
      </div>
    </Panel>
  );
}

export function SkuDetailPage(): JSX.Element {
  const { skuId } = useParams<{ skuId: string }>();
  const t = useT();
  const skusQ = useChannelModelsList();
  const channelsQ = useChannelsList();
  const [presetActivationOpen, setPresetActivationOpen] = useState(false);

  const record = useMemo(
    () => (skusQ.data ?? []).find((cm) => cm.sku_id === skuId) ?? null,
    [skusQ.data, skuId],
  );
  const channel = useMemo(
    () => record === null
      ? null
      : (channelsQ.data ?? []).find((c) => c.id === record.channel_id) ?? null,
    [channelsQ.data, record],
  );
  const skuOption = useMemo<SkuOption | null>(
    () => record === null || channel === null
      ? null
      : {
          skuId: record.sku_id,
          skuName: record.sku_name,
          vendor: channel.vendor,
          model: record.model,
          channelId: channel.id,
        },
    [record, channel],
  );

  if (skusQ.isLoading || channelsQ.isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="sku-detail-loading">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (record === null || channel === null || skuOption === null) {
    return (
      <div
        className="rounded-lg border border-bad-line bg-bad-dim px-4 py-3 text-body text-bad-bright"
        data-testid="sku-detail-not-found"
      >
        {t('sku_detail.not_found')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            <Link
              to="/market"
              aria-label={t('sku_detail.back')}
              className="text-paper-low transition-colors duration-120 hover:text-paper"
            >
              <ArrowLeft aria-hidden="true" className="h-5 w-5" />
            </Link>
            <span className="font-mono">{record.sku_name}</span>
          </span>
        }
        actions={
          // The retired "key preset activation" section was one sentence and a
          // button whose onClick was identical to this one. The sentence is the
          // tooltip now; the duplicate button is gone.
          <Tooltip content={t('sku_detail.keys.hint')}>
            <Button
              variant="primary"
              onClick={() => setPresetActivationOpen(true)}
              data-testid="sku-key-preset-activation-open"
            >
              {t('sku_detail.key_preset_activation')}
            </Button>
          </Tooltip>
        }
      />

      <p className="m-0 flex flex-wrap items-center gap-2 text-body text-paper-low">
        <Chip tone="neutral" lamp={false} className="font-mono">
          {channel.vendor}
        </Chip>
        <span className="inline-flex items-center gap-1">
          {t('sku_detail.namespace')}
          <InfoHint
            label={t('sku_detail.hint.label')}
            content={t('sku_detail.hint.namespace')}
            testId="sku-detail-hint-namespace"
          />{' '}
          <Link
            to={`/supply/namespaces/${encodeURIComponent(channel.id)}`}
            className="font-mono text-signal transition-colors duration-120 hover:text-signal-bright"
            data-testid="sku-detail-namespace-link"
          >
            {channel.slug}
          </Link>
        </span>
        <span>· {t('sku_detail.listed_at')} {formatLocalDate(record.created_at)}</span>
        <span className="font-mono text-micro text-paper-faint">{record.sku_id}</span>
        {/*
          `priced` is the platform's canonical "a model_pricing row is in
          force", published on the catalog record since #1407 — the same
          predicate the shelf gate enforces server-side. It used to be
          `rate_multiplier > 0`, which reads the tenant markup rather than the
          list price and is true for every SKU that exists, so the row could
          show ready while the gate refused.
        */}
        <ReadinessChip
          skuId={record.sku_id}
          priced={record.priced}
          visible={record.org_visibility_default}
        />
      </p>

      {/*
        Two sections, and the anchors the readiness checklist links back to.
        The order is still the sequence an operator walks — price it, then
        give it supply — but "supply" is now one panel instead of the three
        that used to describe `routing_default_sku_targets` separately.
      */}
      <section id="pricing" className="grid items-start gap-6 xl:grid-cols-2">
        <SkuPricingPanel record={record} />
        <SkuStatusPanel skuId={record.sku_id} />
        <SkuRateLimitPanel skuId={record.sku_id} />
        <SkuLoadScopePanel skuId={record.sku_id} namespaceSlug={channel.slug} />
      </section>

      <section id="supply">
        <SupplyPanel sku={skuOption} namespaceChannel={channel} />
      </section>

      {presetActivationOpen ? (
        <KeyPresetActivationDrawer
          open
          skuId={record.sku_id}
          skuName={record.sku_name}
          alias={record.model}
          presetId={channel.id}
          namespaceSlug={channel.slug}
          namespaceDisplayName={channel.display_name}
          onClose={() => setPresetActivationOpen(false)}
        />
      ) : null}
    </div>
  );
}
